import type { CSSProperties, ReactNode } from "react";
import { checkboxSpec, fieldSpec, radioSpec, sliderSpec, switchSpec } from "../specs/controls";
import { mergeVars, typographyClass } from "../specs/types";
import { CheckIcon } from "./Icons";

/* ----------------------------------------------------------------- Switch */

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  id?: string;
}

export function Switch({ checked, onChange, label, disabled, id }: SwitchProps) {
  return (
    <label className={`ui-switch ${typographyClass(switchSpec.labelTypography)}`} style={mergeVars(switchSpec.vars, checkboxSpec.vars) as CSSProperties}>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        data-checked={checked}
        className="ui-switch__track"
        onClick={() => onChange(!checked)}
      >
        <span className="ui-switch__knob" />
      </button>
      {label}
    </label>
  );
}

/* --------------------------------------------------------------- Checkbox */

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
}

export function Checkbox({ checked, onChange, label, disabled }: CheckboxProps) {
  return (
    <label
      className={`ui-checkbox ${typographyClass(checkboxSpec.labelTypography)}`}
      style={checkboxSpec.vars as CSSProperties}
    >
      <span className="ui-checkbox__box" data-checked={checked}>
        {checked && <CheckIcon />}
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        hidden
      />
      {label}
    </label>
  );
}

/* ------------------------------------------------------------------ Radio */

export interface RadioProps {
  checked: boolean;
  onChange: () => void;
  label?: ReactNode;
  name?: string;
}

export function Radio({ checked, onChange, label, name }: RadioProps) {
  return (
    <label className={`ui-radio ${typographyClass(radioSpec.labelTypography)}`} style={radioSpec.vars as CSSProperties}>
      <span className="ui-radio__dot" data-checked={checked} />
      <input type="radio" name={name} checked={checked} onChange={onChange} hidden />
      {label}
    </label>
  );
}

/* ----------------------------------------------------------------- Slider */

export interface SliderProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  suffix?: ReactNode;
}

export function Slider({ value, onChange, min = 0, max = 100, step = 1, disabled, suffix }: SliderProps) {
  return (
    <div className="ui-slider" style={mergeVars(sliderSpec.vars, fieldSpec.base) as CSSProperties}>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {suffix}
    </div>
  );
}
